import { ServiceStatusStatistic } from '@ekkogmbh/apisdk';
import { alpha, Box, Stack, Typography } from '@mui/material';
import { useMemo } from 'react';
import { StatisticElement, statusToColor } from './StatisticElement';
import TooltipHardened from 'src/Common/Components/TooltipHardened';
import KeyValue from 'src/Common/Components/NamedValue';
import { DataUsage } from '@mui/icons-material';

const makeColor = (input: string): string => {
  const statusColor = statusToColor(input);

  if (statusColor) {
    return statusColor[500];
  }

  // generate color from arbitrary input
  let hash = 0;
  input.split('').forEach((char) => {
    hash = char.charCodeAt(0) + ((hash << 5) - hash);
  });
  let colour = '#';
  for (let i = 0; i < 3; i++) {
    const value = (hash >> (i * 8)) & 0xff;
    colour += value.toString(16).padStart(2, '0');
  }
  return colour;
};

interface Props {
  statistic: ServiceStatusStatistic;
}
export function StatisticBar({ statistic }: Props): React.JSX.Element {
  const total = useMemo(() => Object.values(statistic.data).reduce((carry, d) => carry + d), [statistic.data]);

  return (
    <Stack direction={'row'} width={'100%'} spacing={0.5}>
      <TooltipHardened arrow tooltip={<StatisticElement statistic={statistic} stripped />}>
        <Box
          sx={{
            width: 26,
            height: 26,
            borderRadius: 1,
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            border: '1px solid',
            borderColor: 'divider',
            color: 'text.secondary',
            transition: 'border-color 120ms, color 120ms, background-color 120ms',
          }}
        >
          <DataUsage fontSize={'small'} />
        </Box>
      </TooltipHardened>
      <Stack direction={'row'} spacing={0.1} flex={1}>
        {Object.entries(statistic.data).map(([name, value], i) => {
          const width: number = (100 / total) * value;

          return (
            <TooltipHardened key={i} arrow tooltip={<KeyValue name={name} value={value} direction={'row'} />}>
              <Box
                height={24}
                width={width + '%'}
                sx={{
                  bgcolor: (theme) => alpha(makeColor(name), theme.palette.mode === 'dark' ? 0.28 : 0.14),
                }}
              >
                <Typography variant={'caption'} padding={1}>
                  {width < 4 ? ' ' : name}
                </Typography>
              </Box>
            </TooltipHardened>
          );
        })}
      </Stack>
    </Stack>
  );
}
